import { motion } from 'framer-motion';
import { Check, CheckCheck, Lock, AlertCircle, RotateCw, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Message } from '../../types/chat';
import { parseDictionaryContent } from '../../lib/parser';
import { EntryPreviewCard } from './EntryPreviewCard';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X as CloseIcon } from 'lucide-react';

interface MessageBubbleProps {
    message: Message;
    showEncryptionBadge?: boolean;
    onRetry?: () => void;
}

/**
 * Message bubble - Enhanced with status indicators and retry
 */
export function MessageBubble({
    message,
    showEncryptionBadge = false,
    onRetry
}: MessageBubbleProps) {
    const [expandedImage, setExpandedImage] = useState<string | null>(null);
    const isSent = message.sent;
    const status = message.status || 'sent';

    /**
     * Render status indicator for sent messages
     */
    const renderStatusIcon = () => {
        if (!isSent) return null;

        switch (status) {
            case 'sending':
                return <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />;
            case 'sent':
                return <CheckCheck className="w-3 h-3 text-gray-500 dark:text-gray-400" />;
            case 'failed':
                return <AlertCircle className="w-3 h-3 text-red-500" />;
            default:
                return <Check className="w-3 h-3 text-gray-500 dark:text-gray-400" />;
        }
    };

    const topicLinkMatch = message.content.match(/\/topic\/([^\/\s]+)/);
    let topicSlug = null;
    if (topicLinkMatch) {
        try {
            topicSlug = decodeURIComponent(topicLinkMatch[1].split('--')[0]).replace(/-/g, ' ');
        } catch (e) {
            topicSlug = topicLinkMatch[1].split('--')[0].replace(/-/g, ' ');
        }
    }

    // Image URL detection (Blossom or direct links)
    const imageRegex = /(https?:\/\/[^\s]+?\.(?:jpg|jpeg|png|webp|gif|svg)|https?:\/\/blossom[^\s]+)/gi;
    const imageUrls = Array.from(message.content.matchAll(imageRegex)).map(match => match[0]);

    // Filter content to hide raw image links
    const textContent = message.content.replace(imageRegex, '').trim();
    const isImageOnly = imageUrls.length > 0 && textContent === '';

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${isSent ? 'justify-end' : 'justify-start'} mb-3`}
        >
            <div className={`max-w-[75%] ${isSent ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                {/* Message content */}
                <div
                    className={`relative rounded-2xl transition-all ${isSent
                        ? status === 'failed'
                            ? 'bg-red-100 dark:bg-red-900/30 text-gray-900 dark:text-red-200 rounded-br-md border border-red-300 dark:border-red-700 px-4 py-2'
                            : status === 'sending'
                                ? 'bg-gradient-to-br from-mavi-400/50 to-mavi-500/50 text-white rounded-br-md opacity-70 px-4 py-2'
                                : isImageOnly
                                    ? 'bg-transparent'
                                    : 'bg-gradient-to-br from-mavi-500 to-mavi-600 text-white rounded-br-md px-4 py-2'
                        : isImageOnly
                            ? 'bg-transparent'
                            : 'bg-surface-100 dark:bg-surface-800 text-gray-900 dark:text-white rounded-bl-md px-4 py-2'
                        }`}
                >
                    {textContent && (
                        <div
                            className="font-merriweather text-sm whitespace-pre-wrap break-words prose prose-sm dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: parseDictionaryContent(textContent) }}
                        />
                    )}

                    {/* Image Previews */}
                    {imageUrls.length > 0 && (
                        <div className={`${textContent ? 'mt-2' : ''} space-y-2`}>
                            {imageUrls.map((url, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setExpandedImage(url)}
                                    className={`block w-full relative group overflow-hidden rounded-2xl shadow-sm hover:shadow-md transition-shadow cursor-zoom-in ${isSent ? 'ml-auto' : 'mr-auto'
                                        }`}
                                    style={{ maxWidth: '100%', width: 'fit-content' }}
                                >
                                    <img
                                        src={url}
                                        alt="Shared"
                                        loading="lazy"
                                        className="max-w-full max-h-[500px] object-contain rounded-2xl transition-transform duration-700 group-hover:scale-[1.02]"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 dark:group-hover:bg-white/5 transition-colors" />
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Entry Preview Card (if link detected) */}
                    {topicSlug && (
                        <EntryPreviewCard
                            topic={topicSlug}
                            content={message.content.length > 100 ? "Paylaşılan başlığı sözlükte oku." : undefined}
                        />
                    )}

                    {/* Encryption badge (first message) */}
                    {showEncryptionBadge && (
                        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-white/20 dark:border-gray-600/20">
                            <Lock className="w-3 h-3 opacity-70" />
                            <span className="text-[10px] opacity-70">
                                Uçtan uca şifreli
                            </span>
                        </div>
                    )}
                </div>

                {/* Timestamp, status icon, and retry button */}
                <div className="flex items-center gap-2 px-2">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        {formatDistanceToNow(message.timestamp * 1000, {
                            addSuffix: true,
                            locale: tr,
                        })}
                    </span>

                    {renderStatusIcon()}

                    {/* Retry button for failed messages */}
                    {status === 'failed' && onRetry && (
                        <button
                            onClick={onRetry}
                            className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/50 hover:bg-red-200 dark:hover:bg-red-900/70 rounded text-[10px] text-red-700 dark:text-red-300 transition-colors"
                            title="Tekrar gönder"
                        >
                            <RotateCw className="w-3 h-3" />
                            Tekrar dene
                        </button>
                    )}
                </div>
            </div>

            {/* Image Pulse Lightbox / Portal */}
            {expandedImage && createPortal(
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setExpandedImage(null)}
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 lg:p-10 cursor-zoom-out"
                >
                    <button
                        className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                        onClick={() => setExpandedImage(null)}
                    >
                        <CloseIcon className="w-6 h-6" />
                    </button>

                    <motion.img
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        src={expandedImage}
                        alt="Full size"
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl shadow-black/50"
                    />
                </motion.div>,
                document.body
            )}
        </motion.div>
    );
}
